// src/components/Footer.jsx
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const handleLinkClick = (path) => {
    navigate(path);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleHomeClick = () => {
    if (window.location.pathname === '/') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      navigate('/');
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  return (
    <footer className="footer">
      {/* Wave Divider */}
      <div className="footer-wave">
        <svg viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" 
            fill="var(--color-background)"/>
        </svg>
      </div>

      <div className="footer-content">
        <div className="container">
          {/* Main Footer Grid */}
          <div className="footer-grid">
            {/* Brand Section */}
            <div className="footer-section brand-section">
              <div className="brand-logo">
                <div className="logo-icon">
                  <span className="logo-emoji">🌿</span>
                </div>
                <div className="logo-text">
                  <span className="logo-name">Designer DC</span>
                  <span className="logo-tagline">Dispensary & Delivery</span>
                </div>
              </div>
              
              <p className="brand-description">
                Washington DC's premium cannabis delivery service. 
                Fast, discreet, and professional. Lab-tested products 
                delivered to your door within 1 hour.
              </p>

              <div className="social-links">
                <a 
                  href="https://instagram.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label="Instagram"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                    <circle cx="12" cy="12" r="4"/>
                    <circle cx="17.5" cy="6.5" r="1.5"/>
                  </svg>
                </a>
                <a 
                  href="https://facebook.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label="Facebook"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                </a>
                <a 
                  href="https://twitter.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label="Twitter"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="footer-section">
              <h4 className="footer-title">
                <span className="title-decoration"></span>
                Quick Links
              </h4>
              <nav className="footer-nav">
                <button onClick={handleHomeClick} className="footer-link">
                  <span className="link-dot"></span>
                  Home
                </button>
                <button onClick={() => handleLinkClick('/products')} className="footer-link">
                  <span className="link-dot"></span>
                  Products
                </button>
                <button onClick={() => handleLinkClick('/categories')} className="footer-link">
                  <span className="link-dot"></span>
                  Categories
                </button>
                <button onClick={() => handleLinkClick('/about')} className="footer-link">
                  <span className="link-dot"></span>
                  About Us
                </button>
                <button onClick={() => handleLinkClick('/contact')} className="footer-link">
                  <span className="link-dot"></span>
                  Contact
                </button>
              </nav>
            </div>

            {/* Categories */}
            <div className="footer-section">
              <h4 className="footer-title">
                <span className="title-decoration"></span>
                Shop By Category
              </h4>
              <nav className="footer-nav">
                <button onClick={() => handleLinkClick('/category/Flower')} className="footer-link">
                  <span className="link-dot" style={{ background: 'var(--color-flower)' }}></span>
                  Flower
                </button>
                <button onClick={() => handleLinkClick('/category/Exotic-Flower')} className="footer-link">
                  <span className="link-dot" style={{ background: 'var(--color-exotic)' }}></span>
                  Exotic Flower
                </button>
                <button onClick={() => handleLinkClick('/category/Edibles')} className="footer-link">
                  <span className="link-dot" style={{ background: 'var(--color-edibles)' }}></span>
                  Edibles
                </button>
                <button onClick={() => handleLinkClick('/category/Concentrates')} className="footer-link">
                  <span className="link-dot" style={{ background: 'var(--color-concentrates)' }}></span>
                  Concentrates
                </button>
                <button onClick={() => handleLinkClick('/category/cart')} className="footer-link">
                  <span className="link-dot" style={{ background: 'var(--color-cart)' }}></span>
                  Carts
                </button>
                <button onClick={() => handleLinkClick('/category/Pre-rolls')} className="footer-link">
                  <span className="link-dot" style={{ background: 'var(--color-prerolls)' }}></span>
                  Pre-rolls
                </button>
              </nav>
            </div>

            {/* Contact & Hours */}
            <div className="footer-section">
              <h4 className="footer-title">
                <span className="title-decoration"></span>
                Contact Us
              </h4>
              
              <div className="contact-info">
                <a href="tel:2024137664" className="contact-item">
                  <div className="contact-icon">📞</div>
                  <div className="contact-details">
                    <span className="contact-label">Call Us</span>
                    <span className="contact-value">(202) 413-7664</span>
                  </div>
                </a>

                <a href="mailto:info@designerdc.com" className="contact-item">
                  <div className="contact-icon">📧</div>
                  <div className="contact-details">
                    <span className="contact-label">Email Us</span>
                    <span className="contact-value">info@designerdc.com</span>
                  </div>
                </a>

                <div className="contact-item">
                  <div className="contact-icon">📍</div>
                  <div className="contact-details">
                    <span className="contact-label">Location</span>
                    <span className="contact-value">Washington DC</span>
                  </div>
                </div>
              </div>

              <div className="hours-info">
                <h5 className="hours-title">Hours of Operation</h5>
                <div className="hours-grid">
                  <div className="hours-row">
                    <span>Monday - Sunday</span>
                    <span>9:00 AM - 10:00 PM</span>
                  </div>
                  {/* <div className="hours-row">
                    <span>Saturday - Sunday</span>
                    <span>10:00 AM - 12:00 AM</span>
                  </div> */}
                </div>
                <p className="delivery-note">
                  ⚡ 1-hour delivery available in Washington DC
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="footer-bottom">
            <div className="bottom-content">
              <p className="copyright">
                © {currentYear} Designer DC. All rights reserved.
              </p>
              
              <div className="legal-links">
                <button onClick={() => handleLinkClick('/privacy')} className="legal-link">
                  Privacy Policy
                </button>
                <span className="separator">•</span>
                <button onClick={() => handleLinkClick('/terms')} className="legal-link">
                  Terms of Service
                </button>
                <span className="separator">•</span>
                <button onClick={() => handleLinkClick('/faq')} className="legal-link">
                  FAQ
                </button>
              </div>
            </div>
            
            <p className="age-warning">
              Must be 21+ with valid government-issued photo ID. Consume responsibly.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        .footer {
          position: relative;
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: white;
          margin-top: auto;
        }

        /* Wave Divider */
        .footer-wave {
          position: absolute;
          top: -1px;
          left: 0;
          right: 0;
          line-height: 0;
          transform: translateY(-99%);
        }

        .footer-wave svg {
          width: 100%;
          height: clamp(40px, 8vw, 80px);
        }

        @media (prefers-color-scheme: dark) {
          .footer-wave svg path {
            fill: var(--color-background-dark);
          }
        }

        /* Footer Content */
        .footer-content {
          position: relative;
          padding: clamp(2rem, 6vw, 4rem) 0 clamp(1.5rem, 4vw, 2rem);
          z-index: 2;
        }

        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 clamp(1rem, 4vw, 2rem);
        }

        /* Footer Grid */
        .footer-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
          gap: clamp(2rem, 5vw, 3rem);
          margin-bottom: clamp(2rem, 5vw, 3rem);
        }

        /* Brand Section */
        .brand-section {
          grid-column: span 1;
        }

        @media (min-width: 768px) {
          .brand-section {
            grid-column: span 2;
          }
        }

        .brand-logo {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .logo-icon {
          width: clamp(3rem, 6vw, 3.5rem);
          height: clamp(3rem, 6vw, 3.5rem);
          background: rgba(255, 255, 255, 0.15);
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .logo-emoji {
          font-size: clamp(1.5rem, 3vw, 2rem);
        }

        .logo-text {
          display: flex;
          flex-direction: column;
        }

        .logo-name {
          font-size: clamp(1.25rem, 3vw, 1.5rem);
          font-weight: 700;
          line-height: 1.2;
          background: linear-gradient(135deg, #fff, rgba(255, 255, 255, 0.8));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .logo-tagline {
          font-size: 0.8rem;
          opacity: 0.8;
          letter-spacing: 0.05em;
        }

        .brand-description {
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.6;
          margin-bottom: 1.5rem;
          font-size: 0.95rem;
          max-width: 350px;
        }

        /* Social Links */
        .social-links {
          display: flex;
          gap: 1rem;
        }

        .social-link {
          width: 2.5rem;
          height: 2.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          color: white;
          transition: all 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .social-link:hover {
          background: white;
          color: var(--primary-color);
          transform: translateY(-3px);
        }

        .social-link svg {
          width: 1.2rem;
          height: 1.2rem;
        }

        /* Footer Titles */
        .footer-title {
          font-size: clamp(1.1rem, 2.5vw, 1.25rem);
          font-weight: 700;
          margin-bottom: 1.5rem;
          position: relative;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .title-decoration {
          width: 3px;
          height: 1.5rem;
          background: linear-gradient(135deg, #fff, rgba(255, 255, 255, 0.5));
          border-radius: 3px;
        }

        /* Footer Navigation */
        .footer-nav {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .footer-link {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.8);
          text-align: left;
          padding: 0.25rem 0;
          cursor: pointer;
          font-family: inherit;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
        }

        .footer-link:hover {
          color: white;
          transform: translateX(4px);
        }

        .link-dot {
          width: 6px;
          height: 6px;
          background: rgba(255, 255, 255, 0.5);
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .footer-link:hover .link-dot {
          background: white;
          transform: scale(1.5);
        }

        /* Contact Info */
        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          text-decoration: none;
          color: rgba(255, 255, 255, 0.9);
          transition: all 0.3s ease;
          padding: 0.5rem;
          border-radius: 0.75rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .contact-item:hover {
          background: rgba(255, 255, 255, 0.1);
          transform: translateX(4px);
        }

        .contact-icon {
          font-size: 1.25rem;
        }

        .contact-details {
          display: flex;
          flex-direction: column;
        }

        .contact-label {
          font-size: 0.7rem;
          opacity: 0.7;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .contact-value {
          font-size: 0.95rem;
          font-weight: 500;
        }

        /* Hours Info */
        .hours-info {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 1rem;
          padding: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .hours-title {
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          opacity: 0.9;
        }

        .hours-grid {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .hours-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.9);
        }

        .delivery-note {
          font-size: 0.8rem;
          color: var(--accent);
          background: rgba(245, 158, 11, 0.2);
          padding: 0.5rem;
          border-radius: 0.5rem;
          text-align: center;
          font-weight: 500;
        }

        /* Footer Bottom */
        .footer-bottom {
          border-top: 1px solid rgba(255, 255, 255, 0.2);
          padding-top: 1.5rem;
          text-align: center;
        }

        .bottom-content {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        @media (max-width: 640px) {
          .bottom-content {
            flex-direction: column;
            text-align: center;
          }
        }

        .copyright {
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.9rem;
        }

        .legal-links {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.5rem;
          justify-content: center;
        }

        .legal-link {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.8);
          cursor: pointer;
          font-family: inherit;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          padding: 0.25rem 0.5rem;
        }

        .legal-link:hover {
          color: white;
          text-decoration: underline;
        }

        .separator {
          color: rgba(255, 255, 255, 0.4);
        }

        .age-warning {
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.8rem;
          max-width: 600px;
          margin: 0 auto;
        }

        /* Dark Mode Adjustments */
        @media (prefers-color-scheme: dark) {
          .footer {
            background: linear-gradient(135deg, #065f46 0%, #064e3b 100%);
          }
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .footer-grid {
            grid-template-columns: 1fr;
          }

          .brand-section {
            grid-column: span 1;
          }

          .brand-description {
            max-width: 100%;
          }

          .contact-item {
            padding: 0.75rem;
          }

          .legal-links {
            flex-wrap: wrap;
          }
        }

        @media (max-width: 480px) {
          .hours-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
          }

          .legal-links {
            flex-direction: column;
            gap: 0.5rem;
          }

          .separator {
            display: none;
          }
        }

        /* Touch Device Optimizations */
        @media (hover: none) {
          .footer-link:hover,
          .contact-item:hover,
          .social-link:hover {
            transform: none;
          }

          .footer-link:active,
          .contact-item:active {
            background: rgba(255, 255, 255, 0.15);
          }
        }

        /* Print Styles */
        @media print {
          .footer {
            background: #f0f0f0;
            color: black;
          }

          .footer-wave,
          .social-links {
            display: none;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;